import { ChordLib, MelodyLib } from '@tunasong/music-lib'
import type { MelodyChordLane, MelodyMeter } from '@tunasong/schemas'

/**
 * Add chords (melody toolkit M9.2; spec §7): a sketch's chords are typed, one per bar, as chord names Tunasong reads
 * the way a chart does. All or nothing: one name it doesn't recognise, and none are added. Strings approved by Brian
 * on 2026-11-02 (Joni's M9 table), verbatim.
 */

export type ParsedChordNames = { ok: true; symbols: string[] } | { ok: false; unrecognised: string[] }

/** Names separated by spaces, commas or bar lines */
export const parseChordNames = (text: string): ParsedChordNames => {
  const names = text.split(/[\s,|]+/).filter(Boolean)
  const unrecognised = [...new Set(names.filter(name => ChordLib.fromName(name) === undefined))]
  return unrecognised.length > 0 ? { ok: false, unrecognised } : { ok: true, symbols: names }
}

const checkSpelling = 'Check the spelling, for example F#m7b5 or Bb.'
const quoted = (name: string) => `“${name}”`

export const unrecognisedChordsLine = (names: string[]) => {
  const [first = '', second = ''] = names
  if (names.length === 1) {
    return `${quoted(first)} isn't a chord name Tunasong recognises. ${checkSpelling}`
  }
  if (names.length === 2) {
    return `${quoted(first)} and ${quoted(second)} aren't chord names Tunasong recognises. ${checkSpelling}`
  }
  return `${names.length} names aren't chord names Tunasong recognises: ${names.map(quoted).join(', ')}. ${checkSpelling}`
}

/**
 * One chord per bar from the start. Typed by the singer, so its timing is theirs, not a guess to confirm; they drag
 * the edges as in a song
 */
export const laneOfChords = (symbols: string[], meter: MelodyMeter): MelodyChordLane => {
  const bar = MelodyLib.barBeats(meter)
  return { isConfirmed: true, chords: symbols.map((symbol, index) => ({ symbol, onset: index * bar, duration: bar })) }
}
